import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Suspense, lazy, useMemo, type ReactElement } from "react";
import { useParams } from "wouter";
import { MdaiDivider } from "../../components/MdaiDivider";
import { MdaiLink } from "../../components/MdaiLink";
import events from "../../data/events.json";
import type { WholeEvent } from "../../types";
import { EVENT } from "./eventsConstants";
import { NoEvent } from "./NoEvent";

const EventForm = lazy(() => import("./EventForm"));

interface EventPageData {
  title: string;
  date: string;
  headline: string;
  subtext: string;
  footnote: string;
}

function BackToEventsButton() {
  return (
    <Stack
      sx={{
        marginBottom: "32px",
        width: "120px",
        color: "#FFFFFF !important",
      }}
    >
      <MdaiLink
        label={
          <span style={{ display: "flex", alignItems: "center" }}>
            <ArrowBackIcon fontSize="small" />
            &nbsp;Go back
          </span>
        }
        href="/events"
      />
    </Stack>
  );
}

function EventText(props: EventPageData) {
  return (
    <Grid size={6}>
      <BackToEventsButton />
      <Stack direction="column">
        <Chip
          label="In Person"
          sx={{
            maxWidth: "100px",
            marginBottom: "16px",
          }}
          variant="outlined"
        />
        <Typography
          variant="h3"
          sx={{
            fontSize: { lg: "32px" },
            paddingBottom: "32px",
          }}
        >
          {props.title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: "16px" },
          }}
        >
          {props.date}
        </Typography>

        <MdaiDivider
          sx={{
            my: 3,
          }}
        />

        <Typography
          variant="h4"
          sx={{
            fontSize: "24px",
            paddingBottom: "16px",
          }}
        >
          {props.headline}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: "16px",
            paddingBottom: "24px",
          }}
        >
          {props.subtext}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: "16px" },
          }}
        >
          {props.footnote}
        </Typography>
      </Stack>
    </Grid>
  );
}

export default function EventPage(): ReactElement {
  const { slug } = useParams();

  const eventData = useMemo<EventPageData | null>(() => {
    const eventMatch = events.find((evt) => evt.id === slug);
    if (!eventMatch) {
      return null;
    }
    const { shortTitle, date, headline, subtext, footnote } =
      eventMatch as WholeEvent;
    return {
      headline,
      footnote,
      subtext,
      date,
      title: shortTitle,
    };
  }, [slug]);

  if (slug === EVENT.NOCONF) {
    return <NoEvent />;
  }

  if (!eventData) {
    return (
      <Stack>
        <BackToEventsButton />
        Whoops. No events found. Check back soon for more events.
      </Stack>
    );
  }

  return (
    <Box>
      <Container>
        <Stack
          spacing={3}
          alignItems="flex-start"
          justifyContent="center"
          py={10}
          width={"100%"}
          direction={{ sm: "column", lg: "row" }}
        >
          <Stack width={{ sm: "100%", lg: "50%" }}>
            <EventText {...eventData} />
          </Stack>
          <Stack width={{ sm: "100%", lg: "50%" }}>
            <Suspense fallback={null}>
              <EventForm />
            </Suspense>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
